import React, { useState, useMemo } from 'react';
import { Clock } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { SelectField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { IF_PROTOCOLS, calcFastingWindows } from '../../lib/calculatorUtils';

const IntermittentFastingCalculatorPage = () => {
  const [protocolValue, setProtocolValue] = useState('16:8');
  const [startTime, setStartTime] = useState('12:00');

  const protocol = IF_PROTOCOLS.find((p) => p.value === protocolValue) || IF_PROTOCOLS[0];

  const result = useMemo(() => {
    if (!startTime || !/^\d{1,2}:\d{2}$/.test(startTime)) return null;
    return calcFastingWindows(startTime, protocol);
  }, [startTime, protocol]);

  const inputs = (
    <div className="space-y-5" data-testid="if-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Fasting Schedule</h2>
      <p className="text-slate-400 text-sm mb-5">Pick a protocol and your first meal time.</p>
      <SelectField id="if-protocol" label="Protocol" value={protocolValue} onChange={setProtocolValue} options={IF_PROTOCOLS.map((p) => ({ value: p.value, label: p.label }))} testId="if-protocol-select" />
      <div>
        <label htmlFor="if-start" className="block text-sm font-medium text-slate-300 mb-1.5">First Meal Time</label>
        <input
          id="if-start"
          type="time"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          className="w-full bg-slate-800/70 border border-slate-700 focus:border-blue-500 focus:outline-none rounded-xl px-4 py-2.5 text-white text-sm"
          data-testid="if-start-input"
        />
      </div>
      <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
        <p className="text-slate-300 text-sm">
          {protocol.fast}h fast &middot; {protocol.eat}h eating window
        </p>
      </div>
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Eating Window" value={`${result.eatingStart} to ${result.eatingEnd}`} accent="from-teal-500 to-emerald-500" testId="if-result-window" />
      <div className="mt-8 space-y-1">
        <StatRow label="Start eating" value={result.eatingStart} testId="if-eat-start" />
        <StatRow label="Stop eating" value={result.eatingEnd} color="text-emerald-300" testId="if-eat-end" />
        <StatRow label="Fast begins" value={result.fastingStart} testId="if-fast-start" />
        <StatRow label="Fast ends (next meal)" value={result.fastingEnd} color="text-teal-300" testId="if-fast-end" />
        <StatRow label="Eating window" value={`${protocol.eat}`} unit="hours" />
        <StatRow label="Fasting duration" value={`${protocol.fast}`} unit="hours" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Intermittent fasting is not for everyone, particularly those who are pregnant, breastfeeding, underweight, or with certain medical conditions. Consult your physician first.
      </p>
    </div>
  ) : (
    <EmptyResult message="Pick a protocol and your first meal time to see your fasting and eating windows." />
  );

  return (
    <CalculatorLayout
      slug="intermittent-fasting" title="Intermittent Fasting Calculator"
      subtitle="Plan your fasting and eating windows across popular IF protocols such as 16:8, 18:6, OMAD and more."
      metaDescription="Intermittent Fasting Calculator to plan eating and fasting windows for 16:8, 18:6, 20:4, OMAD and 14:10."
      icon={Clock}
      accentGradient="from-teal-500 to-emerald-500" accentColor="text-teal-400"
      badgeBg="bg-teal-500/10" badgeBorder="border-teal-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Protocols:</strong> 16:8 · 18:6 · 20:4 · OMAD (23:1) · 14:10</p>
        <p>Eating window = first meal + N hours. Fast = remaining 24 − N hours.</p>
      </>}
    />
  );
};

export default IntermittentFastingCalculatorPage;
